import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, QrCode, ScanLine, Loader2, UserMinus, ShieldOff, Flag, ChevronLeft, UserPlus, Clock, Check, X } from "lucide-react";

const REPORT_CATEGORIES = [
  { value: "harassment", label: "Harassment or bullying" },
  { value: "spam", label: "Spam or fake content" },
  { value: "fake_profile", label: "Fake or impersonating profile" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "sexual_content", label: "Sexual content involving a minor" },
  { value: "self_harm", label: "Self-harm or suicide content" },
  { value: "violence", label: "Threats or violence" },
  { value: "other", label: "Something else" },
];

export default function FriendsHome() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [reportTarget, setReportTarget] = useState(null);
  const [reportCategory, setReportCategory] = useState("");
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  // Bootstrap — gate on social setup completion, then pull friends + requests
  useEffect(() => {
    (async () => {
      try {
        const p = await api.get("/me/social");
        setProfile(p.data);
        if (!p.data.social_setup_complete) {
          nav("/student/friends/setup", { replace: true });
          return;
        }
        const [f, r] = await Promise.all([
          api.get("/friends"),
          api.get("/friends/requests"),
        ]);
        setFriends(f.data.friends || []);
        setRequests({
          incoming: r.data.incoming || [],
          outgoing: r.data.outgoing || [],
        });
      } catch (e) { /* ignored */ }
    })();
  }, [nav]);

  const refresh = async () => {
    const [f, r] = await Promise.all([api.get("/friends"), api.get("/friends/requests")]);
    setFriends(f.data.friends || []);
    setRequests({ incoming: r.data.incoming || [], outgoing: r.data.outgoing || [] });
  };

  const accept = async (id) => { await api.post(`/friends/requests/${id}/accept`); toast.success("Friend added"); refresh(); };
  const decline = async (id) => { await api.post(`/friends/requests/${id}/decline`); refresh(); };
  const cancel = async (id) => { await api.post(`/friends/requests/${id}/cancel`); refresh(); };
  const remove = async (id, handle) => {
    if (!confirm(`Remove ${handle} from your friends?`)) return;
    await api.delete(`/friends/${id}`); toast.success("Removed"); refresh();
  };
  const block = async (id, handle) => {
    if (!confirm(`Block ${handle}? You won't see each other's profiles or activity, and you'll be unfriended.`)) return;
    await api.post(`/friends/${id}/block`); toast.success("Blocked"); refresh();
  };

  const submitReport = async () => {
    if (!reportCategory) return;
    setReportBusy(true);
    try {
      await api.post("/reports", {
        target_type: "user",
        target_id: reportTarget.id,
        category: reportCategory,
        note: reportNote,
      });
      toast.success("Reported. Our team will review within 24 hours.");
      setReportTarget(null);
      setReportCategory("");
      setReportNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit report");
    } finally {
      setReportBusy(false);
    }
  };

  if (!profile) {
    return <div className="flex items-center justify-center min-h-[40vh] text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="friends-home">
      <Link to="/student" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Friends
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
            {profile.display_handle || "Friends"}
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Connect with study buddies via QR codes. Your full name and email are never shared.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/student/friends/qr">
          <Card className="p-5 border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer h-full" data-testid="friends-show-qr-card">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-md bg-indigo-100 text-indigo-700">
                <QrCode className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">My QR Code</div>
                <div className="text-xs text-slate-500">Show it to a friend to connect</div>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/student/friends/scan">
          <Card className="p-5 border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer h-full" data-testid="friends-scan-card">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-md bg-amber-100 text-amber-700">
                <ScanLine className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Scan a Friend</div>
                <div className="text-xs text-slate-500">Open camera to scan their QR</div>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {requests.incoming.length > 0 && (
        <Card className="p-5 border-amber-200 bg-amber-50/50" data-testid="friends-incoming-card">
          <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-amber-700" /> Friend Requests ({requests.incoming.length})
          </h3>
          <div className="space-y-2">
            {requests.incoming.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-white p-3 rounded-md border border-amber-200" data-testid={`incoming-req-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{r.from?.display_handle || "Unknown"}</div>
                  <div className="text-[11px] text-slate-500">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => accept(r.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`accept-${r.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decline(r.id)} data-testid={`decline-${r.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {requests.outgoing.length > 0 && (
        <Card className="p-5 border-slate-200" data-testid="friends-outgoing-card">
          <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2 text-slate-600">
            <Clock className="w-4 h-4" /> Pending Invites ({requests.outgoing.length})
          </h3>
          <div className="space-y-2">
            {requests.outgoing.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-slate-200">
                <div className="text-sm text-slate-700">Waiting for <strong>{r.to?.display_handle}</strong></div>
                <Button size="sm" variant="ghost" onClick={() => cancel(r.id)} data-testid={`cancel-${r.id}`}>
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5 border-slate-200" data-testid="friends-list-card">
        <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-600" /> Your Friends ({friends.length})
        </h3>
        {friends.length === 0 ? (
          <div className="text-center py-10 space-y-3" data-testid="friends-empty-state">
            <div className="mx-auto h-14 w-14 rounded-full bg-indigo-100 grid place-items-center">
              <Users className="w-7 h-7 text-indigo-600" strokeWidth={1.5} />
            </div>
            <div className="text-base font-semibold text-slate-900">Study is better together</div>
            <p className="text-sm text-slate-600 max-w-xs mx-auto">
              Add your first study buddy — scan their QR or share yours, then cheer each other on with streak nudges.
            </p>
            <a href="#your-qr" className="inline-block">
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white mt-1" data-testid="friends-empty-share-qr">
                Show My QR Code
              </Button>
            </a>
          </div>
        ) : (
          <div className="space-y-2">
            {friends.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-3 p-3 rounded-md border border-slate-200" data-testid={`friend-${f.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {f.display_handle}
                    {f.is_minor && <Badge variant="outline" className="text-[10px] py-0">protected</Badge>}
                  </div>
                  <div className="text-[11px] text-slate-500">Friends since {new Date(f.accepted_at).toLocaleDateString()}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setReportTarget(f)} data-testid={`report-${f.id}`}>
                    <Flag className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => block(f.id, f.display_handle)} data-testid={`block-${f.id}`}>
                    <ShieldOff className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => remove(f.id, f.display_handle)} data-testid={`remove-${f.id}`}>
                    <UserMinus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="text-center text-[11px] text-slate-500">
        Issues? <Link to="/legal/community-guidelines" className="text-indigo-600 hover:underline">Community Guidelines</Link>
        {" · "}<Link to="/student/account" className="text-indigo-600 hover:underline">Privacy Settings</Link>
      </p>

      <Dialog open={!!reportTarget} onOpenChange={(o) => !o && setReportTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-rose-600" /> Report {reportTarget?.display_handle}</DialogTitle>
            <DialogDescription>
              Help us keep PrepMasters safe. Reports are reviewed within 24 hours. Severe categories
              hide the content immediately pending review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={reportCategory} onValueChange={setReportCategory}>
              <SelectTrigger data-testid="report-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent>
                {REPORT_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`report-cat-${c.value}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              placeholder="Optional details (max 1000 chars)"
              maxLength={1000}
              rows={3}
              data-testid="report-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportTarget(null)}>Cancel</Button>
            <Button
              onClick={submitReport}
              disabled={!reportCategory || reportBusy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="report-submit-btn"
            >
              {reportBusy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
